import { useState, useEffect, useRef } from "react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL, money, fmtDate, onboardingLink } from "../lib/helpers";
import { UserPlus, Plus, Trash, Users, Stack, PencilSimple, EnvelopeSimple, Storefront, Copy, Eye, Archive, ArrowCounterClockwise, House, UploadSimple, ArrowsDownUp, MagnifyingGlass, UserSwitch } from "@phosphor-icons/react";
import MemberProfile from "../components/MemberProfile";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const EMPTY_USER = { email: "", name: "", role: "agent", tier: "", can_photograph: false, can_pilot: false };
const ROLE_ORDER = { admin: 0, employee: 1, agent: 2, photographer: 3, pilot: 4 };

export default function AdminPanel({ users, reload }) {
  const { user: me, impersonate } = useAuth();
  const isAdmin = me.role === "admin";
  const canViewAs = (u) => u.id !== me.id && (isAdmin ? u.role !== "admin" : ["agent", "photographer", "pilot", "dual"].includes(u.role));
  const [tiers, setTiers] = useState([]);
  const [viewingAs, setViewingAs] = useState(null);
  const [userDlg, setUserDlg] = useState(false);
  const [tierDlg, setTierDlg] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [uForm, setUForm] = useState(EMPTY_USER);
  const [tForm, setTForm] = useState({ name: "", rate: "", edit_fee: "" });
  const [confirmDel, setConfirmDel] = useState(null);
  const [viewMember, setViewMember] = useState(null);
  // Referral-page editor (admin manages a photographer's public /p/{slug} page)
  const [refUser, setRefUser] = useState(null);
  const [rForm, setRForm] = useState({ slug: "", bio: "", gallery: [] });
  const [refImg, setRefImg] = useState("");
  const [refSlugMsg, setRefSlugMsg] = useState(null);
  const [savingRef, setSavingRef] = useState(false);
  const [archivedShoots, setArchivedShoots] = useState([]);
  const [confirmPurge, setConfirmPurge] = useState(null);
  const refGalleryRef = useRef(null);
  const [uploadingRefImg, setUploadingRefImg] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [roleSort, setRoleSort] = useState("asc");
  const [memberSearch, setMemberSearch] = useState("");

  const openRef = (u) => {
    setRefUser(u);
    setRForm({ slug: u.slug || "", bio: u.bio || "", gallery: u.gallery || [] });
    setRefImg(""); setRefSlugMsg(null);
  };
  const checkRefSlug = async (v) => {
    const s = v.trim().toLowerCase();
    setRForm((f) => ({ ...f, slug: s }));
    if (!s) { setRefSlugMsg(null); return; }
    try { const r = await client.get("/slug/check", { params: { slug: s, exclude: refUser.id } }); setRefSlugMsg(r.data); }
    catch { setRefSlugMsg(null); }
  };
  const addRefImg = () => {
    const u = refImg.trim();
    if (!u) return;
    if (rForm.gallery.length >= 8) return toast.error("Up to 8 images");
    setRForm((f) => ({ ...f, gallery: [...f.gallery, u] })); setRefImg("");
  };
  const removeRefImg = (i) => setRForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));
  const onRefGalleryFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !refUser) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Use a PNG, JPEG or WEBP image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    if (rForm.gallery.length >= 8) return toast.error("Up to 8 images");
    setUploadingRefImg(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await client.post(`/users/${refUser.id}/gallery-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRForm((f) => ({ ...f, gallery: [...f.gallery, r.data.url] }));
      toast.success("Photo added — click Save page to publish");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploadingRefImg(false); }
  };
  const saveRef = async () => {
    if (rForm.slug && refSlugMsg && !refSlugMsg.available) { toast.error("Choose an available link"); return; }
    setSavingRef(true);
    try {
      await client.patch(`/users/${refUser.id}/referral`, { slug: rForm.slug || null, bio: rForm.bio, gallery: rForm.gallery });
      toast.success("Referral page updated");
      setRefUser(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSavingRef(false); }
  };
  const refUrl = onboardingLink(refUser?.role || "photographer", refUser?.slug);

  const loadTiers = () => client.get("/tiers").then((r) => setTiers(r.data));
  useEffect(() => { loadTiers(); }, []);

  const openAdd = () => { setEditingUser(null); setUForm(EMPTY_USER); setUserDlg(true); };
  const openEdit = (u) => {
    setEditingUser(u);
    setUForm({ email: u.email, name: u.name || "", role: u.role, tier: u.tier || "",
               can_photograph: !!u.can_photograph, can_pilot: !!u.can_pilot });
    setUserDlg(true);
  };
  const closeUserDlg = () => { setUserDlg(false); setEditingUser(null); setUForm(EMPTY_USER); };

  const saveUser = async () => {
    if (!uForm.email) { toast.error("Email required"); return; }
    const payload = {
      email: uForm.email, name: uForm.name, role: uForm.role,
      tier: (uForm.role === "photographer" || uForm.role === "pilot" || uForm.role === "dual") ? (uForm.tier || null) : null,
      can_photograph: uForm.role === "pilot" ? !!uForm.can_photograph : false,
      can_pilot: uForm.role === "photographer" ? !!uForm.can_pilot : false,
    };
    try {
      if (editingUser) {
        await client.patch(`/users/${editingUser.id}`, payload);
        toast.success("Member updated");
      } else {
        await client.post("/users", payload);
        toast.success("Member added — they can sign in with their email");
      }
      closeUserDlg();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delUser = async (u) => {
    try {
      await client.delete(`/users/${u.id}`);
      toast.success(`${u.name || u.email} removed`);
      setConfirmDel(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove member");
      setConfirmDel(null);
    }
  };

  const saveTier = async () => {
    if (!tForm.name || tForm.rate === "") { toast.error("Name and rate required"); return; }
    await client.post("/tiers", { name: tForm.name, rate: Number(tForm.rate), edit_fee: Number(tForm.edit_fee || 0) });
    toast.success("Tier created");
    setTierDlg(false); setTForm({ name: "", rate: "", edit_fee: "" });
    loadTiers();
  };
  const delTier = async (id) => { await client.delete(`/tiers/${id}`); loadTiers(); };

  const resendInvite = async (u) => {
    try {
      const r = await client.post(`/users/${u.id}/reset-link`);
      if (r.data?.reset_link) { try { await navigator.clipboard.writeText(r.data.reset_link); } catch {} }
      toast.success(`Magic sign-in link sent to ${u.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send invite"); }
  };

  const activeUsers = users.filter((u) => u.active !== false);
  const archivedUsers = users.filter((u) => u.active === false);
  const roleCounts = {
    all: activeUsers.length,
    admin: activeUsers.filter((u) => u.role === "admin").length,
    employee: activeUsers.filter((u) => u.role === "employee").length,
    agent: activeUsers.filter((u) => u.role === "agent").length,
    photographer: activeUsers.filter((u) => u.role === "photographer").length,
    pilot: activeUsers.filter((u) => u.role === "pilot").length,
    dual: activeUsers.filter((u) => u.role === "dual").length,
  };
  const _q = memberSearch.trim().toLowerCase();
  const displayedUsers = activeUsers
    .filter((u) => roleFilter === "all" || u.role === roleFilter)
    .filter((u) => !_q || (u.name || "").toLowerCase().includes(_q) || (u.email || "").toLowerCase().includes(_q))
    .slice()
    .sort((a, b) => {
      const d = (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9);
      return roleSort === "asc" ? d : -d;
    });

  const loadArchivedShoots = () => client.get("/properties/archived").then((r) => setArchivedShoots(r.data)).catch(() => {});
  const restoreUser = async (u) => {
    try { await client.post(`/users/${u.id}/restore`); toast.success(`${u.name || u.email} restored`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not restore"); }
  };
  const restoreShoot = async (p) => {
    try { await client.post(`/properties/${p.id}/restore`); toast.success("Shoot restored"); loadArchivedShoots(); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not restore"); }
  };
  const purgeShoot = async (p) => {
    try { await client.delete(`/properties/${p.id}/permanent`); toast.success("Shoot permanently deleted"); setConfirmPurge(null); loadArchivedShoots(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); setConfirmPurge(null); }
  };

  if (viewMember) {
    return <MemberProfile userId={viewMember} tiers={tiers} onBack={() => setViewMember(null)} onChanged={reload} />;
  }

  return (
    <Tabs defaultValue="team" data-testid="admin-panel">
      <TabsList className="bg-[#FAF9F6] mb-6">
        <TabsTrigger value="team" data-testid="tab-team"><Users size={16} className="mr-1" />Members</TabsTrigger>
        {isAdmin && <TabsTrigger value="tiers" data-testid="tab-tiers" onClick={loadTiers}><Stack size={16} className="mr-1" />Pay Tiers</TabsTrigger>}
        {isAdmin && <TabsTrigger value="archived" data-testid="tab-archived" onClick={loadArchivedShoots}><Archive size={16} className="mr-1" />Archived</TabsTrigger>}
      </TabsList>

      <TabsContent value="team">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="member-role-tabs">
            {[["all", "All"], ["admin", "Admins"], ["employee", "Employees"], ["agent", "Agents"], ["photographer", "Photographers"], ["pilot", "Pilots"], ["dual", "Dual"]].map(([r, lbl]) => (
              <button key={r} data-testid={`member-role-tab-${r}`} onClick={() => setRoleFilter(r)}
                className={`px-3 py-1.5 rounded-[5px] text-sm font-medium border transition-colors ${roleFilter === r ? "bg-[#4A0000] text-white border-[#4A0000]" : "bg-white text-[#5C5A58] border-[#E9DDC6] hover:border-[#4A0000]/40"}`}>
                {lbl} <span className={`ml-1 text-xs ${roleFilter === r ? "text-white/70" : "text-[#9A968E]"}`}>{roleCounts[r]}</span>
              </button>
            ))}
            <Button variant="outline" data-testid="member-role-sort" onClick={() => setRoleSort((s) => (s === "asc" ? "desc" : "asc"))} className="border-[#E9DDC6] ml-1" size="sm" title="Sort by role">
              <ArrowsDownUp size={16} className="mr-1.5" />Role {roleSort === "asc" ? "A–Z" : "Z–A"}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A968E]" />
              <Input data-testid="member-search" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search name or email" className="pl-9 w-56 bg-white border-[#E9DDC6]" />
            </div>
            <Button data-testid="add-user-button" onClick={openAdd} className={`bg-[#4A0000] hover:bg-[#5C1A1A] text-white ${isAdmin ? "" : "hidden"}`}><UserPlus size={18} className="mr-1" />Add Member</Button>
          </div>
        </div>
        <div className="border border-[#E9DDC6] rounded-md overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#FAF9F6] text-left text-[#5C5A58]"><th className="p-4 font-medium">Name</th><th className="p-4 font-medium">Email</th><th className="p-4 font-medium">Role</th><th className="p-4 font-medium">Tier</th><th className="p-4 text-right">Actions</th></tr></thead>
            <tbody>
              {displayedUsers.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#9A968E]">No members match this filter.</td></tr>}
              {displayedUsers.map((u, i) => (
                <tr key={u.id} className={i % 2 ? "bg-[#FBFAF7]" : "bg-white"} data-testid={`user-row-${u.id}`}>
                  <td className="p-4 font-medium"><button data-testid={`view-user-${u.id}`} onClick={() => setViewMember(u.id)} className="text-left hover:text-[#4A0000] hover:underline underline-offset-2 transition-colors">{u.name || u.email}</button></td>
                  <td className="p-4 font-mono-num text-xs">{u.email}</td>
                  <td className="p-4"><Badge className="bg-[#4A0000]/10 text-[#4A0000] border-[#4A0000]/20">{ROLE_LABEL[u.role]}</Badge></td>
                  <td className="p-4">{u.tier || "—"}</td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => setViewMember(u.id)} className="text-[#4A0000] hover:text-[#5C1A1A]" data-testid={`profile-user-${u.id}`} title="View profile"><Eye size={16} /></button>
                      {isAdmin && <button onClick={() => openEdit(u)} className="text-[#4A0000] hover:text-[#5C1A1A]" data-testid={`edit-user-${u.id}`} title="Edit member"><PencilSimple size={16} /></button>}
                      {canViewAs(u) && (
                        <button onClick={async () => { setViewingAs(u.id); try { await impersonate(u.id, u.name || u.email); } catch (e) { toast.error(e.response?.data?.detail || "Could not view as this member"); setViewingAs(null); } }}
                          disabled={viewingAs === u.id} className="text-[#B08D57] hover:text-[#8A6D2E] disabled:opacity-50" data-testid={`view-as-${u.id}`} title="View & edit as this member"><UserSwitch size={16} /></button>
                      )}
                      {u.role === "photographer" && <button onClick={() => openRef(u)} className="text-[#4A0000] hover:text-[#5C1A1A]" data-testid={`edit-referral-${u.id}`} title="Manage booking page"><Storefront size={16} /></button>}
                      {isAdmin && <button onClick={() => resendInvite(u)} className="text-[#4A0000] hover:text-[#5C1A1A]" data-testid={`resend-invite-${u.id}`} title="Send + copy password reset link"><EnvelopeSimple size={16} /></button>}
                      {isAdmin && u.id !== me.id && <button onClick={() => setConfirmDel(u)} className="text-red-600 hover:text-red-800" data-testid={`del-user-${u.id}`} title="Remove member"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TabsContent>

      <TabsContent value="tiers">
        <div className="flex justify-end mb-4">
          <Button data-testid="add-tier-button" onClick={() => setTierDlg(true)} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white"><Plus size={18} className="mr-1" />Add Tier</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((t) => (
            <div key={t.id} className="border border-[#E9DDC6] rounded-md p-6 bg-white" data-testid={`tier-${t.id}`}>
              <div className="flex justify-between items-start">
                <h3 className="font-display text-2xl">{t.name}</h3>
                <button onClick={() => delTier(t.id)} className="text-red-600 hover:text-red-800"><Trash size={16} /></button>
              </div>
              <p className="font-display text-3xl text-[#4A0000] mt-2">{money(t.rate)}</p>
              <p className="text-sm text-[#5C5A58]">per shoot · +{money(t.edit_fee)} edit fee</p>
            </div>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="archived">
        <div className="space-y-8" data-testid="archived-panel">
          <div>
            <h3 className="font-display text-xl mb-1 flex items-center gap-2 text-[#2C2A28]"><Users size={18} weight="duotone" className="text-[#4A0000]" />Archived members ({archivedUsers.length})</h3>
            <p className="text-sm text-[#8A8782] mb-3">Removed members keep their history. Restore to give them access again.</p>
            <div className="border border-[#E9DDC6] rounded-md overflow-hidden bg-white shadow-premium">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#FAF9F6] text-left text-[#5C5A58]"><th className="p-4 font-medium">Name</th><th className="p-4 font-medium">Email</th><th className="p-4 font-medium">Role</th><th className="p-4 text-right">Actions</th></tr></thead>
                <tbody>
                  {archivedUsers.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#9A968E]">No archived members.</td></tr>}
                  {archivedUsers.map((u, i) => (
                    <tr key={u.id} className={i % 2 ? "bg-[#FBFAF7]" : "bg-white"} data-testid={`archived-user-${u.id}`}>
                      <td className="p-4 font-medium">{u.name || "—"}</td>
                      <td className="p-4 font-mono-num text-xs">{u.email}</td>
                      <td className="p-4"><Badge className="bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6]">{ROLE_LABEL[u.role]}</Badge></td>
                      <td className="p-4 text-right"><Button size="sm" variant="outline" data-testid={`restore-user-${u.id}`} onClick={() => restoreUser(u)} className="border-[#E9DDC6] hover:bg-[#4A0000]/5"><ArrowCounterClockwise size={15} className="mr-1.5" />Restore</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="font-display text-xl mb-1 flex items-center gap-2 text-[#2C2A28]"><House size={18} weight="duotone" className="text-[#4A0000]" />Archived shoots ({archivedShoots.length})</h3>
            <p className="text-sm text-[#8A8782] mb-3">Restore a removed shoot, or delete it forever (this can't be undone).</p>
            <div className="border border-[#E9DDC6] rounded-md overflow-hidden bg-white shadow-premium">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#FAF9F6] text-left text-[#5C5A58]"><th className="p-4 font-medium">Shoot</th><th className="p-4 font-medium">Agent</th><th className="p-4 font-medium">Photographer</th><th className="p-4 font-medium">Removed</th><th className="p-4 text-right">Actions</th></tr></thead>
                <tbody>
                  {archivedShoots.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#9A968E]">No archived shoots.</td></tr>}
                  {archivedShoots.map((p, i) => (
                    <tr key={p.id} className={i % 2 ? "bg-[#FBFAF7]" : "bg-white"} data-testid={`archived-shoot-${p.id}`}>
                      <td className="p-4"><p className="font-medium">{p.title}</p><p className="text-xs text-[#8A8782] truncate max-w-[220px]">{p.address}</p></td>
                      <td className="p-4">{p.agent || "—"}</td>
                      <td className="p-4">{p.photographer || "—"}</td>
                      <td className="p-4 text-xs text-[#8A8782]">{p.deleted_at ? fmtDate(p.deleted_at) : "—"}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" data-testid={`restore-shoot-${p.id}`} onClick={() => restoreShoot(p)} className="border-[#E9DDC6] hover:bg-[#4A0000]/5"><ArrowCounterClockwise size={15} className="mr-1.5" />Restore</Button>
                          <Button size="sm" variant="outline" data-testid={`purge-shoot-${p.id}`} onClick={() => setConfirmPurge(p)} className="border-red-200 text-red-600 hover:bg-red-50"><Trash size={15} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </TabsContent>

      {/* Referral / booking-page editor (photographers) */}
      <Dialog open={!!refUser} onOpenChange={(v) => { if (!v) setRefUser(null); }}>
        <DialogContent className="bg-[#FBFAF7] max-h-[90vh] overflow-y-auto" data-testid="referral-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl">Booking page</DialogTitle><DialogDescription>{refUser && <>Manage <b>{refUser.name || refUser.email}</b>'s public /p/ page — link, bio and gallery.</>}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Page link</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#5C5A58] whitespace-nowrap">/p/</span>
                <Input data-testid="referral-slug" value={rForm.slug} onChange={(e) => checkRefSlug(e.target.value)} placeholder="jane-doe" maxLength={15} className="bg-white border-[#E9DDC6] font-mono-num" />
              </div>
              {refSlugMsg && <p data-testid="referral-slug-msg" className={`text-xs mt-1 ${refSlugMsg.available ? "text-green-700" : "text-red-600"}`}>{refSlugMsg.reason}</p>}
              {refUrl && (
                <div className="flex items-center gap-2 mt-2">
                  <a href={refUrl} target="_blank" rel="noreferrer" data-testid="referral-link" className="text-sm text-[#4A0000] underline underline-offset-2 truncate">{refUrl}</a>
                  <Button variant="outline" size="sm" data-testid="referral-copy" onClick={() => { navigator.clipboard.writeText(refUrl); toast.success("Link copied"); }} className="border-[#E9DDC6] shrink-0"><Copy size={15} /></Button>
                </div>
              )}
            </div>
            <div>
              <Label>Short bio</Label>
              <Textarea data-testid="referral-bio" value={rForm.bio} onChange={(e) => setRForm((f) => ({ ...f, bio: e.target.value }))} maxLength={280} rows={3} placeholder="A sentence or two shown on the booking page." className="bg-white border-[#E9DDC6]" />
              <p className="text-xs text-[#5C5A58] mt-1">{rForm.bio.length}/280</p>
            </div>
            <div>
              <Label>Sample-work gallery</Label>
              <div className="flex items-center gap-2">
                <Input data-testid="referral-gallery-input" value={refImg} onChange={(e) => setRefImg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRefImg())} placeholder="Paste an image URL…" className="bg-white border-[#E9DDC6]" />
                <Button type="button" data-testid="referral-gallery-add" onClick={addRefImg} variant="outline" className="border-[#E9DDC6] shrink-0">Add</Button>
                <Button type="button" data-testid="referral-gallery-upload" onClick={() => refGalleryRef.current?.click()} disabled={uploadingRefImg} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white shrink-0"><UploadSimple size={16} className="mr-1" weight="bold" />{uploadingRefImg ? "Uploading…" : "Upload"}</Button>
                <input ref={refGalleryRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onRefGalleryFile} className="hidden" data-testid="referral-gallery-file" />
              </div>
              {rForm.gallery.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3" data-testid="referral-gallery-thumbs">
                  {rForm.gallery.map((src, i) => (
                    <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-[#E9DDC6] group">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button type="button" data-testid={`referral-gallery-remove-${i}`} onClick={() => removeRefImg(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#4A0000] text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#5C5A58] mt-1">Up to 8 photos. First ones show first.</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRefUser(null)} className="border-[#E9DDC6]">Cancel</Button><Button data-testid="referral-save" onClick={saveRef} disabled={savingRef} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{savingRef ? "Saving…" : "Save page"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / edit member dialog */}
      <Dialog open={userDlg} onOpenChange={(v) => { if (!v) closeUserDlg(); }}>
        <DialogContent className="bg-[#FBFAF7]" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editingUser ? "Edit Member" : "Add Member"}</DialogTitle><DialogDescription>{editingUser ? "Update this member's details and role." : "Invite a new member and assign their role."}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Email</Label><Input data-testid="user-email" value={uForm.email} disabled={!!editingUser} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} className="bg-white border-[#E9DDC6] disabled:bg-[#FAF9F6]" />{editingUser && <p className="text-xs text-[#5C5A58] mt-1">Email is the sign-in identity and can't be changed.</p>}</div>
            <div><Label>Name</Label><Input data-testid="user-name" value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} className="bg-white border-[#E9DDC6]" /></div>
            <div><Label>Role</Label>
              <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v })}>
                <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="agent">Agent</SelectItem><SelectItem value="photographer">Photographer</SelectItem><SelectItem value="pilot">Pilot</SelectItem><SelectItem value="dual">Dual (Photo + Drone)</SelectItem><SelectItem value="employee">Employee</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </div>
            {uForm.role === "dual" && (
              <p className="text-sm text-[#5C5A58] bg-[#FAF9F6] rounded-md px-3 py-2" data-testid="dual-role-note">
                A <b>Dual</b> member shoots <b>and</b> flies drone. On any shoot needing both, claiming or being assigned auto-fills the <b>photographer</b> and <b>pilot</b> slots together.
              </p>
            )}
            {uForm.role === "photographer" && (
              <label className="flex items-center gap-2.5 text-sm text-[#1A1715] cursor-pointer" data-testid="user-can-pilot-label">
                <input type="checkbox" data-testid="user-can-pilot" checked={!!uForm.can_pilot} onChange={(e) => setUForm({ ...uForm, can_pilot: e.target.checked })} className="w-4 h-4 accent-[#4A0000]" />
                Also flies drone (can fill the <b>pilot</b> slot on All-Inclusive shoots)
              </label>
            )}
            {uForm.role === "pilot" && (
              <label className="flex items-center gap-2.5 text-sm text-[#1A1715] cursor-pointer" data-testid="user-can-photograph-label">
                <input type="checkbox" data-testid="user-can-photograph" checked={!!uForm.can_photograph} onChange={(e) => setUForm({ ...uForm, can_photograph: e.target.checked })} className="w-4 h-4 accent-[#4A0000]" />
                Also shoots (can fill the <b>photographer</b> slot)
              </label>
            )}
            {(uForm.role === "photographer" || uForm.role === "pilot" || uForm.role === "dual") && (
              <div><Label>Tier</Label>
                <Select value={uForm.tier} onValueChange={(v) => setUForm({ ...uForm, tier: v })}>
                  <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="user-tier"><SelectValue placeholder="Select tier" /></SelectTrigger>
                  <SelectContent>{tiers.map((t) => <SelectItem key={t.id} value={t.name}>{t.name} · {money(t.rate)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={closeUserDlg} className="border-[#E9DDC6]">Cancel</Button><Button data-testid="user-save" onClick={saveUser} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{editingUser ? "Save changes" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add tier dialog */}
      <Dialog open={tierDlg} onOpenChange={setTierDlg}>
        <DialogContent className="bg-[#FBFAF7]" data-testid="tier-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl">Add Pay Tier</DialogTitle><DialogDescription>Name the tier and set its base and edit fees.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Name</Label><Input data-testid="tier-name" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} className="bg-white border-[#E9DDC6]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Rate ($)</Label><Input data-testid="tier-rate" type="number" value={tForm.rate} onChange={(e) => setTForm({ ...tForm, rate: e.target.value })} className="bg-white border-[#E9DDC6] font-mono-num" /></div>
              <div><Label>Edit fee ($)</Label><Input data-testid="tier-editfee" type="number" value={tForm.edit_fee} onChange={(e) => setTForm({ ...tForm, edit_fee: e.target.value })} className="bg-white border-[#E9DDC6] font-mono-num" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setTierDlg(false)} className="border-[#E9DDC6]">Cancel</Button><Button data-testid="tier-save" onClick={saveTier} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Remove member confirmation */}
      <AlertDialog open={!!confirmDel} onOpenChange={(v) => { if (!v) setConfirmDel(null); }}>
        <AlertDialogContent className="bg-[#FBFAF7]" data-testid="confirm-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Remove this member?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel && <>This revokes <b>{confirmDel.name || confirmDel.email}</b>'s access. They will no longer be able to sign in. You can re-add them later with the same email.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E9DDC6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-remove-button" onClick={() => delUser(confirmDel)} className="bg-red-600 hover:bg-red-700 text-white">Remove access</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanently delete shoot confirmation */}
      <AlertDialog open={!!confirmPurge} onOpenChange={(v) => { if (!v) setConfirmPurge(null); }}>
        <AlertDialogContent className="bg-[#FBFAF7]" data-testid="confirm-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete this shoot forever?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmPurge && <>This permanently removes <b>{confirmPurge.title}</b> and its messages. This cannot be undone.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E9DDC6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-purge-button" onClick={() => purgeShoot(confirmPurge)} className="bg-red-600 hover:bg-red-700 text-white">Delete forever</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
  );
}
